<template>
  <main class="gs-app gm-studio">
    <gs-card class="gm-header gm-page-header">
      <div>
        <router-link to="/forms/new">← New Groove forms</router-link>
        <h1>{{ name || 'Untitled form' }}</h1>
        <span class="gm-status" :class="'gm-status--' + status">{{
          status
        }}</span>
        <small aria-live="polite">{{
          notice || (dirty ? 'Unsaved changes' : '')
        }}</small>
      </div>
      <div class="gm-actions">
        <gs-button
          variant="outline"
          :disabled="saving || loading || loadFailed"
          @click="save(status === 'published' ? 'published' : 'draft')"
          >{{ saving ? 'Saving…' : 'Save changes' }}</gs-button
        ><gs-button
          :disabled="saving || loading || loadFailed"
          @click="step < 3 ? step++ : save('published')"
          >{{
            step < 3
              ? 'Continue'
              : status === 'published'
              ? 'Update published form'
              : 'Publish form'
          }}</gs-button
        >
      </div>
    </gs-card>
    <div v-if="error" class="gm-error" role="alert">{{ error }}</div>
    <p v-if="loading" role="status">Loading form…</p>
    <template v-else-if="!loadFailed">
      <div v-if="status === 'archived'" class="gm-panel gm-archive-notice" role="status">
        <div><strong>This form is archived</strong><p>Editing is paused and visitors cannot submit. Restore it as a draft to add, edit, or reorder fields.</p></div>
        <gs-button :disabled="saving" @click="save('draft')">Restore draft &amp; edit</gs-button>
      </div>
      <nav class="gm-tabs" aria-label="Form setup">
        <button
          v-for="(label, index) in steps"
          :key="label"
          :class="{ active: step === index }"
          :aria-current="step === index ? 'step' : null"
          @click="step = index"
        >
          {{ index + 1 }}. {{ label }}
        </button>
      </nav>
      <div v-if="step === 0" class="gm-panel gm-stack">
        <div>
          <h2>Give your form a name</h2>
          <p class="gm-muted">
            This is how you’ll find it here and in GroovePages.
          </p>
        </div>
        <label class="gm-field"
          >Form name<input
            v-model="name"
            maxlength="160"
            placeholder="e.g. Weekly newsletter"
            autofocus
        /></label>
        <p class="gm-muted">
          The name is for you. Visitors see the heading you set in the builder.
        </p>
      </div>
      <mail-baser-form-canvas v-if="step === 1" :document="document" :name="name" :archived="status === 'archived'" @change="document = $event" />
      <div v-if="step === 2" class="gm-stack">
        <nav class="gm-tabs" aria-label="Form behavior">
          <button v-for="tab in ['audience', 'notifications', 'confirmation']" :key="tab" :class="{ active: behaviorTab === tab }" @click="behaviorTab = tab">{{ title(tab) }}</button>
        </nav>
        <div v-if="optionsError" class="gm-error" role="alert">
          {{ optionsError }}
          <gs-button variant="outline" @click="loadOptions"
            >Reload lists and senders</gs-button
          >
        </div>
        <div v-show="behaviorTab === 'audience'" class="gm-panel gm-stack">
          <h2>Audience</h2>
          <p class="gm-muted">
            Connect this form to your GrooveMail lists, tags and sequences. Hold
            Command or Control to select more than one.
          </p>
          <div class="gm-two">
            <label
              v-for="key in ['lists', 'tags', 'sequences']"
              :key="key"
              class="gm-field"
              >{{ title(key)
              }}<select
                v-model="document.behavior[key]"
                multiple
                :aria-label="title(key)"
                ><option
                  v-for="item in options[key]"
                  :key="item.id"
                  :value="item.id"
                  >{{ item.name }}</option
                ></select
              ><small v-if="!options[key].length"
                >No {{ key }} available. Create them in GrooveMail first.</small
              ></label
            >
          </div>
          <label class="gm-field"
            >Subscription confirmation<select v-model="document.behavior.optin"
              ><option value="single"
                >Single opt-in — subscribe immediately</option
              ><option value="double"
                >Double opt-in — confirm by email first</option
              ></select
            ></label
          >
        </div>
        <div
          v-if="document.behavior.optin === 'double' && behaviorTab === 'audience'"
          class="gm-panel gm-stack"
        >
          <h2>Confirmation email</h2>
          <label class="gm-field"
            >Verified sender<select v-model="document.behavior.confirmSenderId"
              ><option value="">Choose a sender</option
              ><option
                v-for="sender in options.senders"
                :key="sender.id"
                :value="sender.id"
                >{{ sender.name }}</option
              ></select
            ></label
          ><label class="gm-field"
            >Subject<input v-model="document.behavior.confirmSubject"
          /></label>
          <label class="gm-field"
            >Preview text<input
              v-model="document.behavior.confirmPreviewText"
              maxlength="500"
          /></label>
          <p class="gm-muted">
            Keep <code>{optin_confirmation_link}</code> in the email so
            subscribers can confirm.
          </p>
          <studio-email-editor
            :value="document.behavior.confirmHtml"
            :restricted="true"
            @input="document.behavior.confirmHtml = $event"
            @template-selected="
              document.behavior.confirmSubject = $event.subject;
              $set(document.behavior, 'confirmPreviewText', $event.previewText);
            "
          />
        </div>
        <div v-show="behaviorTab === 'notifications'" class="gm-panel gm-stack">
          <h2>Notifications</h2>
          <label class="gm-check"
            ><input
              v-model="document.behavior.defaultAccountEmail"
              type="checkbox"
            />Forward responses to my account email</label
          ><label class="gm-field"
            >Forward responses to verified recipients<select
              v-model="document.behavior.recipients"
              multiple
              ><option
                v-for="item in options.recipients"
                :key="item.id"
                :value="item.id"
                >{{ item.name }}</option
              ></select
            ><small
              >Only verified recipients appear here. Manage them in GrooveMail
              settings.</small
            ></label
          >
        </div>
        <div v-show="behaviorTab === 'confirmation'" class="gm-panel gm-stack">
          <h2>After submission</h2>
          <label class="gm-field"
            >Thank-you message<textarea
              v-model="document.behavior.successMessage"/></label
          ><label class="gm-field"
            >Redirect URL (optional)<input
              v-model="document.behavior.redirectUrl"
              type="url"
              placeholder="https://your-site.com/thank-you"
            /><small
              >Leave empty to show your thank-you message. Double opt-in asks
              visitors to check their inbox.</small
            ></label
          >
        </div>
        <div v-show="behaviorTab === 'confirmation'" class="gm-panel gm-stack">
          <h2>Welcome email</h2>
          <label class="gm-check"
            ><input
              v-model="document.behavior.welcomeEnabled"
              type="checkbox"
            />Send a welcome email after subscription</label
          ><template v-if="document.behavior.welcomeEnabled"
            ><label class="gm-field"
              >Verified sender<select
                v-model="document.behavior.welcomeSenderId"
                ><option value="">Choose a sender</option
                ><option
                  v-for="sender in options.senders"
                  :key="sender.id"
                  :value="sender.id"
                  >{{ sender.name }}</option
                ></select
              ></label
            ><label class="gm-field"
              >Subject<input v-model="document.behavior.welcomeSubject"/></label
            ><label class="gm-field"
              >Preview text<input
                v-model="document.behavior.welcomePreviewText"
                maxlength="500"/></label
            ><studio-email-editor
              :value="document.behavior.welcomeHtml"
              :restricted="true"
              @input="document.behavior.welcomeHtml = $event"
              @template-selected="
                document.behavior.welcomeSubject = $event.subject;
                $set(
                  document.behavior,
                  'welcomePreviewText',
                  $event.previewText
                );
              "
          /></template>
        </div>
      </div>
      <div v-if="step === 3" class="gm-split">
        <div class="gm-canvas-surface">
          <form-canvas :design="document.design" :fields="document.fields" />
        </div>
        <div class="gm-stack">
          <div class="gm-panel">
            <h2>Ready to connect</h2>
            <p>
              {{ document.fields.length }} fields ·
              {{ document.behavior.lists.length }} lists ·
              {{ document.behavior.tags.length }} tags ·
              {{ document.behavior.sequences.length }} sequences
            </p>
            <p class="gm-muted">
              Published forms appear in the GroovePages form selector. Drafts
              and archived forms aren’t public.
            </p>
            <div v-if="status === 'published' && id" class="gm-stack">
              <a :href="publicUrl" target="_blank" rel="noopener"
                >Open hosted form ↗</a
              >
              <form-embed-options :id="id" :name="name" :document="document" />
              <p class="gm-muted">
                In GroovePages: add a GrooveMail Form element, then choose “{{
                  name
                }}”. The form’s design, lists and tags stay connected.
              </p>
            </div>
          </div>
          <div v-if="id" class="gm-panel">
            <h3>
              {{
                status === 'archived'
                  ? 'Restore this form'
                  : 'Take this form offline'
              }}
            </h3>
            <p class="gm-muted">
              {{
                status === 'archived'
                  ? 'Save as a draft or publish to make it available again.'
                  : 'Archiving keeps the form and its history. Visitors can no longer submit.'
              }}
            </p>
            <gs-button
              v-if="status !== 'archived'"
              variant="danger"
              @click="archiveConfirm = true"
              >Archive form</gs-button
            >
          </div>
        </div>
      </div>
      <div class="gm-actions" style="justify-content:space-between;margin-top:24px" v-if="step < 3">
        <gs-button variant="outline" :disabled="step === 0" @click="step--">Back</gs-button>
        <gs-button @click="step++">{{ ['Continue to builder', 'Continue to behavior', 'Review & publish'][step] }}</gs-button>
      </div>
    </template>
    <form-subscribers
      v-if="id && step === 3"
      :id="id"
      style="margin-top:24px"
    />
    <gs-modal v-model="archiveConfirm" title="Archive this form?"
      ><p>
        Existing subscriber data stays in GrooveMail. This form will no longer
        accept submissions or appear in the GroovePages selector.
      </p>
      <div class="gm-actions">
        <gs-button variant="outline" @click="archiveConfirm = false"
          >Keep form</gs-button
        ><gs-button
          variant="danger"
          @click="
            archiveConfirm = false;
            save('archived');
          "
          >Archive form</gs-button
        >
      </div></gs-modal
    >
    <gs-modal v-model="leaveConfirm" title="Leave without saving?"
      ><p>Your last saved form will remain unchanged.</p>
      <div class="gm-actions">
        <gs-button variant="outline" @click="leaveConfirm = false"
          >Keep editing</gs-button
        ><gs-button variant="danger" @click="leave"
          >Discard unsaved changes</gs-button
        >
      </div></gs-modal
    >
  </main>
</template>
<script>
import './index';
import axios from 'axios';
import FormCanvas from './FormCanvas.vue';
import MailBaserFormCanvas from './MailBaserFormCanvas.vue';
import FormEmbedOptions from './FormEmbedOptions.vue';
import FormSubscribers from './FormSubscribers.vue';
import StudioEmailEditor from './StudioEmailEditor.vue';
import templates from './data/form-templates.json';
import {
  formWidthOptions,
  formFontOptions,
  formButtonIcons
} from './data/form-design';
import {
  newForm,
  errorMessage,
  publicFormUrl,
  validateEmailHtml
} from './contracts';
export default {
  middleware: 'auth',
  layout: 'defaultWithoutVuePerfectScrollbar',
  components: {
    FormCanvas,
    MailBaserFormCanvas,
    StudioEmailEditor,
    FormEmbedOptions,
    FormSubscribers
  },
  data() {
    const template =
      templates.find(item => item.id === this.$route.query.template) ||
      templates[templates.length - 1];
    return {
      id: '',
      name: template.name,
      document: newForm(template),
      status: 'draft',
      saved: '',
      step: 0,
      steps: ['Name', 'Build', 'Behavior', 'Review & publish'],
      selection: 'form',
      behaviorTab: 'audience',
      mobile: false,
      loading: false,
      loadFailed: false,
      saving: false,
      error: '',
      notice: '',
      optionsError: '',
      options: {
        lists: [],
        tags: [],
        sequences: [],
        senders: [],
        recipients: []
      },
      widths: formWidthOptions,
      fonts: formFontOptions,
      icons: formButtonIcons,
      fieldTypes: [
        'text',
        'email',
        'phone',
        'number',
        'textarea',
        'select',
        'checkbox',
        'consent',
        'hidden'
      ],
      archiveConfirm: false,
      leaveConfirm: false,
      pendingRoute: null
    };
  },
  computed: {
    selectedField() {
      return typeof this.selection === 'number'
        ? this.document.fields[this.selection]
        : null;
    },
    snapshot() {
      return JSON.stringify({
        name: this.name,
        document: this.document,
        status: this.status
      });
    },
    dirty() {
      return this.saved !== this.snapshot;
    },
    publicUrl() {
      return publicFormUrl(this.id);
    }
  },
  async created() {
    this.saved = this.snapshot;
    this.loadOptions();
    if (this.$route.params.id !== 'new') {
      this.loading = true;
      try {
        const form = (await axios.get('/studio/forms/' + this.$route.params.id))
          .data;
        this.id = form.id;
        this.name = form.name;
        this.document = form.document;
        this.status = form.status;
        this.step = 1;
        this.saved = this.snapshot;
      } catch (error) {
        this.loadFailed = true;
        this.error = errorMessage(error);
      } finally {
        this.loading = false;
      }
    }
  },
  mounted() {
    window.addEventListener('beforeunload', this.beforeUnload);
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.beforeUnload);
  },
  beforeRouteLeave(to, from, next) {
    if (this.dirty) {
      this.pendingRoute = to.fullPath;
      this.leaveConfirm = true;
      next(false);
    } else next();
  },
  methods: {
    title(text) {
      return text[0].toUpperCase() + text.slice(1);
    },
    beforeUnload(event) {
      if (this.dirty) {
        event.preventDefault();
        event.returnValue = '';
      }
    },
    leave() {
      this.saved = this.snapshot;
      this.leaveConfirm = false;
      this.$router.push(this.pendingRoute);
    },
    async loadOptions() {
      this.optionsError = '';
      try {
        this.options = (await axios.get('/studio/forms/options')).data;
      } catch (error) {
        this.optionsError = errorMessage(error);
      }
    },
    addField() {
      if (this.document.fields.length >= 50) return;
      let number = this.document.fields.length + 1;
      while (
        this.document.fields.some(field => field.key === 'field_' + number)
      )
        number++;
      this.document.fields.push({
        key: 'field_' + number,
        label: 'New field',
        type: 'text',
        placeholder: '',
        required: false,
        options: [],
        width: 'full'
      });
      this.selection = this.document.fields.length - 1;
    },
    removeField() {
      if (this.selectedField.key !== 'email') {
        this.document.fields.splice(this.selection, 1);
        this.selection = 'form';
      }
    },
    moveField(direction) {
      const target = this.selection + direction;
      if (target < 0 || target >= this.document.fields.length) return;
      const item = this.document.fields.splice(this.selection, 1)[0];
      this.document.fields.splice(target, 0, item);
      this.selection = target;
    },
    async save(status) {
      if (this.loadFailed) return;
      if (this.saving) return;
      this.error = '';
      this.notice = '';
      if (!this.name.trim()) {
        this.step = 0;
        this.error = 'Give your form a name.';
        return;
      }
      this.saving = true;
      try {
        validateEmailHtml(this.document.behavior.confirmHtml || '');
        validateEmailHtml(this.document.behavior.welcomeHtml || '');
        const payload = {
          name: this.name.trim(),
          status,
          document: this.document
        };
        const result = this.id
          ? await axios.put('/studio/forms/' + this.id, payload)
          : await axios.post('/studio/forms', payload);
        this.id = result.data.id;
        this.status = result.data.status;
        this.saved = this.snapshot;
        this.notice =
          status === 'published'
            ? 'Published. Available in GroovePages.'
            : status === 'archived'
            ? 'Archived. Submissions are disabled.'
            : 'Draft saved.';
        if (this.$route.params.id === 'new')
          await this.$router.replace('/forms/studio/' + this.id);
      } catch (error) {
        this.error = errorMessage(error);
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.gm-archive-notice { display:flex; align-items:center; justify-content:space-between; gap:16px; border:1px solid #d79c42; background:#fff8e8; margin:16px 0; }
.gm-archive-notice p { margin:6px 0 0; }
@media(max-width:600px) { .gm-archive-notice { align-items:flex-start; flex-direction:column; } }
</style>
